<template>
  <div>
    <h3>Dependencies</h3>

    <!-- Filters -->
    <dependency-filters
      :repositories="repositories"
      :repository-branches="repositoryBranches"
      :initial-selected-repositories="initialSelectedRepositories"
      :selected-branches="selectedBranchesByRepo"
      :show-branch-selector="showBranchSelector"
      v-model:language-filter="languageFilter"
      v-model:version-filter="versionFilter"
      v-model:security-filter="securityFilter"
      v-model:selected-repositories="selectedRepositories"
      @branch-change="onBranchChange"
    />

    <!-- Loading Spinner -->
    <div v-if="isLoading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Stats Cards -->
    <dependency-stats-cards v-if="!isLoading" :stats="auditStats" />

    <!-- Dependencies Table -->
    <template v-if="!isLoading">
      <div v-if="dependenciesSorted.length === 0" class="alert alert-info my-3">
        No dependencies found. Go to the repository page to sync branches, create builds and fetch dependencies.
      </div>
      <table v-else class="table table-hover" style="table-layout: fixed">
        <thead>
          <tr>
            <th width="15%" v-if="repositories.length > 0" @click="sortBy('repository')" class="sortable">
              Repository
              <i
                v-if="sortKey === 'repository'"
                :class="sortOrder === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"
              ></i>
            </th>
            <th width="10%" @click="sortBy('language')" class="sortable">
              Language
              <i v-if="sortKey === 'language'" :class="sortOrder === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
            </th>
            <th width="15%" @click="sortBy('name')" class="sortable">
              Name
              <i v-if="sortKey === 'name'" :class="sortOrder === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
            </th>
            <th width="10%" @click="sortBy('current_version')" class="sortable">
              Current version
              <i
                v-if="sortKey === 'current_version'"
                :class="sortOrder === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"
              ></i>
            </th>
            <th width="10%" @click="sortBy('latest_version')" class="sortable">
              Latest version
              <i
                v-if="sortKey === 'latest_version'"
                :class="sortOrder === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"
              ></i>
            </th>
            <th width="10%" @click="sortBy('cvss_v3_score')" class="sortable">
              CVSS v3 Score
              <i
                v-if="sortKey === 'cvss_v3_score'"
                :class="sortOrder === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"
              ></i>
            </th>
            <th width="30%">Security warnings</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(dependency, idx) in dependenciesSorted" :key="idx" :class="getRowClass(dependency)">
            <td class="text-break" v-if="repositories.length > 0">
              {{ dependency.build?.repository?.title }}
            </td>
            <td class="text-break">{{ dependency.language }}</td>
            <td class="text-break">{{ dependency.name }}</td>
            <td class="text-break">{{ dependency.current_version }}</td>
            <td class="text-break">{{ dependency.latest_version }}</td>
            <td class="text-break">{{ dependency.cvss_v3_score }}</td>
            <td>
              <button
                v-if="dependency.security_warnings"
                @click="showSecurityWarnings(dependency)"
                class="btn btn-sm btn-danger"
                title="Click to view security warnings"
              >
                <i class="fa fa-exclamation-triangle"></i> View Warnings
              </button>
              <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- Security Warnings Modal -->
    <security-warnings-modal :show="showModal" :dependency="selectedDependency" @close="closeModal" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { compareSemVer, isValidSemVer, parseSemVer } from "semver-parser";
import DependencyFilters from "./DependencyFilters.vue";
import DependencyStatsCards from "./DependencyStatsCards.vue";
import SecurityWarningsModal from "./SecurityWarningsModal.vue";

// ============================================================================
// Props
// ============================================================================
const props = defineProps({
  dependencies: {
    type: Array,
    default: () => []
  },
  repositories: {
    type: Array,
    default: () => []
  },
  repositoryGroups: {
    type: Array,
    default: () => []
  },
  availableBranches: {
    type: Array,
    default: () => []
  },
  selectedBranchesByRepo: {
    type: Object,
    default: () => ({})
  },
  repositoryBranches: {
    type: Object,
    default: () => ({})
  },
  initialSelectedRepositories: {
    type: Array,
    default: () => []
  },
  showBranchSelector: {
    type: Boolean,
    default: true
  }
});

// ============================================================================
// State
// ============================================================================
const languageFilter = ref("");
const versionFilter = ref("");
const securityFilter = ref("");
const selectedRepositories = ref([...props.initialSelectedRepositories]);
const dependencies = ref([...props.dependencies]);
const selectedBranchesByRepo = ref({ ...props.selectedBranchesByRepo });
const isLoading = ref(false);
const showModal = ref(false);
const selectedDependency = ref(null);
const sortKey = ref("priority");
const sortOrder = ref("asc");

// ============================================================================
// Lifecycle Hooks
// ============================================================================
onMounted(() => {
  fetchDependencies(selectedBranchesByRepo.value);
});

// ============================================================================
// Watchers
// ============================================================================
watch(selectedRepositories, (newRepos, oldRepos) => {
  const removedRepos = oldRepos.filter((repoId) => !newRepos.includes(repoId));

  removedRepos.forEach((repoId) => {
    delete selectedBranchesByRepo.value[repoId];
  });

  if (removedRepos.length > 0) {
    fetchDependencies(selectedBranchesByRepo.value);
  }
});

// ============================================================================
// API Methods
// ============================================================================
async function fetchDependencies(branchesByRepo) {
  isLoading.value = true;

  try {
    const params = new URLSearchParams();

    selectedRepositories.value.forEach((repoId) => {
      params.append("repositories[]", repoId);
    });

    Object.entries(branchesByRepo).forEach(([repoId, branch]) => {
      params.append(`branches[${repoId}]`, branch);
    });

    const response = await fetch(`/filter_dependencies?${params.toString()}`);
    const data = await response.json();

    dependencies.value = data.dependencies;
    selectedBranchesByRepo.value = data.selected_branches_by_repo;
  } catch (error) {
    console.error("Failed to fetch dependencies:", error);
  } finally {
    isLoading.value = false;
  }
}

// ============================================================================
// Event Handlers
// ============================================================================
function onBranchChange(branchesByRepo) {
  fetchDependencies(branchesByRepo);
}

function showSecurityWarnings(dependency) {
  selectedDependency.value = dependency;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  selectedDependency.value = null;
}

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
}

// ============================================================================
// Helper Methods
// ============================================================================
function rubyGemToSemVer(version) {
  return version.replace(/\.([a-zA-Z]+)/g, "-$1").replace(/([0-9]+)\.([0-9]+)\.([0-9]+)\.([0-9]+)/, "$1.$2.$3+$4");
}

function getRowClass(dependency) {
  return {
    "table-warning": dependency.meta.outdated,
    "table-danger": dependency.meta.hasSecurityWarnings,
    "table-success": dependency.meta.upToDate
  };
}

function hasSecurityWarnings(dependency) {
  return dependency.security_warnings !== null && dependency.security_warnings.length > 0;
}

function calculateVersionMeta(dependency) {
  const currentVersion = rubyGemToSemVer(dependency.current_version);
  const latestVersion = rubyGemToSemVer(dependency.latest_version);
  const hasWarnings = hasSecurityWarnings(dependency);

  // If versions are not valid semver, do simple comparison
  if (!isValidSemVer(currentVersion) || !isValidSemVer(latestVersion)) {
    const outdated = currentVersion !== latestVersion;
    return {
      outdated,
      upToDate: !outdated && !hasWarnings,
      hasSecurityWarnings: hasWarnings,
      outdatedVersionParts: outdated ? ["major", "minor", "patch"] : []
    };
  }

  // Parse semver versions
  const currentParsed = parseSemVer(currentVersion);
  const latestParsed = parseSemVer(latestVersion);
  let outdatedVersionParts = [];

  // Determine which version parts are outdated
  if (latestParsed.major > currentParsed.major) {
    outdatedVersionParts.push("major");
  } else if (latestParsed.minor > currentParsed.minor) {
    outdatedVersionParts.push("minor");
  } else if (latestParsed.patch > currentParsed.patch) {
    outdatedVersionParts.push("patch");
  } else if ((latestParsed.build ?? []).join(",") !== (currentParsed.build ?? []).join(",")) {
    outdatedVersionParts.push("patch");
  }

  // If current version is ahead of latest, it's not outdated
  if (compareSemVer(currentVersion, latestVersion) === 1) {
    outdatedVersionParts = [];
  }

  const outdated = outdatedVersionParts.length > 0;

  return {
    outdated,
    upToDate: !outdated && !hasWarnings,
    hasSecurityWarnings: hasWarnings,
    outdatedVersionParts
  };
}

function getSortPriority(dependency) {
  if (dependency.meta.hasSecurityWarnings) return 1;
  if (dependency.meta.outdated) return 2;
  if (dependency.meta.upToDate) return 3;
  return 4;
}

// ============================================================================
// Computed Properties
// ============================================================================
const dependenciesMapped = computed(() => {
  return dependencies.value.map((dependency) => ({
    ...dependency,
    meta: calculateVersionMeta(dependency)
  }));
});

const dependenciesFiltered = computed(() => {
  return dependenciesMapped.value
    .filter((dependency) => {
      // Filter by selected repositories
      if (selectedRepositories.value.length === 0) {
        return true;
      }

      const repositoryId = dependency.build?.repository?.id;
      if (!repositoryId) {
        return false;
      }

      return selectedRepositories.value.includes(repositoryId.toString());
    })
    .filter((dependency) => {
      // Filter by language
      if (languageFilter.value !== "" && dependency.language !== languageFilter.value) {
        return false;
      }

      // Filter by version status
      if (versionFilter.value !== "") {
        if (versionFilter.value === "uptodate" && dependency.meta.outdated) {
          return false;
        } else if (versionFilter.value === "outdated" && !dependency.meta.outdated) {
          return false;
        }

        if (versionFilter.value === "major" && !dependency.meta.outdatedVersionParts.includes("major")) {
          return false;
        } else if (versionFilter.value === "minor" && !dependency.meta.outdatedVersionParts.includes("minor")) {
          return false;
        } else if (versionFilter.value === "patch" && !dependency.meta.outdatedVersionParts.includes("patch")) {
          return false;
        }
      }

      // Filter by security warnings
      if (
        securityFilter.value !== "" &&
        ((dependency.security_warnings === null && securityFilter.value === "only") ||
          (dependency.security_warnings !== null && securityFilter.value === "except"))
      ) {
        return false;
      }

      return true;
    });
});

const dependenciesSorted = computed(() => {
  const filtered = [...dependenciesFiltered.value];

  return filtered.sort((a, b) => {
    let aVal, bVal;

    switch (sortKey.value) {
      case "priority":
        aVal = getSortPriority(a);
        bVal = getSortPriority(b);
        break;
      case "repository":
        aVal = a.build?.repository?.title ?? "";
        bVal = b.build?.repository?.title ?? "";
        break;
      case "language":
        aVal = a.language;
        bVal = b.language;
        break;
      case "name":
        aVal = a.name;
        bVal = b.name;
        break;
      case "current_version":
        aVal = a.current_version;
        bVal = b.current_version;
        break;
      case "latest_version":
        aVal = a.latest_version;
        bVal = b.latest_version;
        break;
      case "cvss_v3_score":
        aVal = a.cvss_v3_score ?? 0;
        bVal = b.cvss_v3_score ?? 0;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortOrder.value === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder.value === "asc" ? 1 : -1;
    return 0;
  });
});

const auditStats = computed(() => {
  const filteredDeps = dependenciesFiltered.value;
  const count = filteredDeps.length;
  const upToDateCount = filteredDeps.filter((d) => d.meta.upToDate).length;
  const scorePercent =
    count > 0
      ? new Intl.NumberFormat("da-DK", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format((upToDateCount / count) * 100) + "%"
      : "0%";

  const filteredOutdated = filteredDeps.filter((d) => d.meta.outdated).length;
  const filteredHasSecurityWarning = filteredDeps.filter((d) => d.meta.hasSecurityWarnings).length;
  const filteredUpToDate = filteredDeps.filter((d) => d.meta.upToDate).length;

  return {
    scorePercent,
    filteredCount: count,
    filteredOutdated,
    filteredHasSecurityWarning,
    filteredUpToDate
  };
});
</script>

<style scoped>
[v-cloak] {
  display: none;
}

.text-break {
  overflow-wrap: break-word;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: rgb(0, 0, 0, 0.05);
}

.sortable i {
  margin-left: 0.5rem;
}
</style>
